import { resolveTenantDatabaseName } from "./resolveTenantDatabaseName";

/** Chaves localStorage usadas para tenant/projeto no app do workspace (não inclui auth, UI global, chat). */
const WORKSPACE_LOCALSTORAGE_KEYS = [
  "company",
  "xproWorkspaceDatabase",
  "activeProjectId",
  "recentCompanies",
  "perm_scopes_cache",
];

/** Chaves sessionStorage ligadas a empresa ativa / cache de workspace (alinhado ao que o logout limpa para escopo workspace). */
const WORKSPACE_SESSIONSTORAGE_KEYS = [
  "activeCompany",
  "selectedCompanies",
  "selectedCompany",
  "workspace-config-cache",
  "perm_scopes_cache",
];

/**
 * Master com JWT `scope=workspace` continua autenticando no schema do tenant (`usuarios`, `permissoes`);
 * o header `Company` não pode ser `xprobase` nesse caso — a API admin lê o cadastro central nos handlers.
 */
function shouldPreserveTenantDatabaseKeysForWorkspaceMaster() {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("userData") : null;
    if (!raw) return false;
    const ud = JSON.parse(raw);
    if (Number(ud?.master) !== 1) return false;
    const scope = ud?.scope || "workspace";
    if (scope === "platform" || scope === "partner") return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Remove persistência de workspace/tenant usada pelo app principal ao entrar no console da plataforma
 * ou no portal revenda — evita ids de projeto e caches defasados.
 *
 * Opção `preserveTenantDatabaseKeysForWorkspaceMaster`: mantém `company` e `xproWorkspaceDatabase` quando
 * o usuário é master com sessão de workspace (JWT no tenant). Assim o `verifyToken` não conecta ao schema
 * meta `xprobase` como se fosse tenant (onde não existem `projetos` / `permissoes`).
 *
 * Não remove: `bearerToken`, `userData`, preferências de layout, chaves de chat globais, `workspace-setup-*`
 * (ficam por projeto e não afetam as rotas master/partner).
 *
 * @param {{ preserveTenantDatabaseKeysForWorkspaceMaster?: boolean }} [options]
 */
export function clearActiveWorkspaceBrowserStorage(options = {}) {
  let preserveTenantDb =
    Boolean(options.preserveTenantDatabaseKeysForWorkspaceMaster) &&
    shouldPreserveTenantDatabaseKeysForWorkspaceMaster();
  let savedCompany = null;
  let savedMirror = null;
  if (preserveTenantDb) {
    try {
      savedCompany = localStorage.getItem("company");
      savedMirror = localStorage.getItem("xproWorkspaceDatabase");
    } catch {
      /* ignore */
    }
    const slug = String(savedCompany || "").trim().toLowerCase();
    if (!slug || slug === "xprobase") {
      preserveTenantDb = false;
      savedCompany = null;
      savedMirror = null;
    }
  }
  try {
    if (typeof localStorage !== "undefined") {
      for (const k of WORKSPACE_LOCALSTORAGE_KEYS) {
        try {
          if (preserveTenantDb && (k === "company" || k === "xproWorkspaceDatabase")) continue;
          localStorage.removeItem(k);
        } catch {
          /* ignore */
        }
      }
    }
    if (typeof sessionStorage !== "undefined") {
      for (const k of WORKSPACE_SESSIONSTORAGE_KEYS) {
        try {
          sessionStorage.removeItem(k);
        } catch {
          /* ignore */
        }
      }
      // Metadados ETag do bundle i18n por workspace (`useRemoteI18nBundle`); prefixo estável no código.
      const extra = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith("i18n_bundle_meta_")) extra.push(key);
      }
      for (const key of extra) {
        try {
          sessionStorage.removeItem(key);
        } catch {
          /* ignore */
        }
      }
    }
    if (preserveTenantDb) {
      const co = savedCompany != null ? String(savedCompany).trim() : "";
      if (co) {
        try {
          localStorage.setItem("company", co);
          const mir = savedMirror != null && String(savedMirror).trim() ? String(savedMirror).trim() : co;
          localStorage.setItem("xproWorkspaceDatabase", mir);
        } catch {
          /* ignore */
        }
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Se o workspace removido for o mesmo ativo no browser (`openWorkspaceInApp`),
 * limpa `company`, `xproWorkspaceDatabase` (localStorage) e `activeCompany` (sessionStorage).
 *
 * @param {string | null | undefined} dominioHost ex.: alex.xpro.me
 * @param {string | null | undefined} bdRaw coluna dominios.bd
 */
export function clearStoredWorkspaceIfExcluded(dominioHost, bdRaw) {
  const slug = resolveTenantDatabaseName(dominioHost, bdRaw);
  if (!slug) return;
  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const nSlug = norm(slug);
  if (!nSlug) return;
  try {
    const company = typeof localStorage !== "undefined" ? localStorage.getItem("company") : null;
    const xproDb = typeof localStorage !== "undefined" ? localStorage.getItem("xproWorkspaceDatabase") : null;
    const active = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("activeCompany") : null;
    const matches = [company, xproDb, active].some((v) => norm(v) === nSlug);
    if (!matches) return;
    localStorage.removeItem("company");
    localStorage.removeItem("xproWorkspaceDatabase");
    sessionStorage.removeItem("activeCompany");
  } catch {
    /* ignore */
  }
}
